import React, { useState } from 'react';
import type { AvatarProps } from './Avatar.types';

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'Avatar',
  name,
  icon,
  size = 'md',
  shape = 'circle',
  status,
  statusRingWidth = 'ring-2',
  statusRingColor = 'ring-white dark:ring-zinc-900',
  className = '',
}) => {
  const [imageError, setImageError] = useState(false);

  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-14 h-14 text-xl',
  };

  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-md',
  };

  const statusColors = {
    online: 'bg-lime-400 ',
    offline: 'bg-zinc-400',
    busy: 'bg-red-400',
    away: 'bg-yellow-400',
  };

  const statusSizes = {
    xs: 'w-1.5 h-1.5 ring-1',
    sm: 'w-2 h-2 ring-1',
    md: 'w-2.5 h-2.5 ring-2',
    lg: 'w-3 h-3 ring-2',
    xl: 'w-3.5 h-3.5 ring-2',
  };

  // Adjust position depending on shape
  const statusPositionClasses = shape === 'circle'
    ? 'bottom-0 right-0'
    : '-bottom-1 -right-1';

  // Translate ring width correctly if a custom one is provided, otherwise keep default sizes
  const statusDotClasses = [
    'absolute',
    'rounded-full',
    statusPositionClasses,
    statusColors[status as keyof typeof statusColors],
    statusSizes[size].replace(/ring-\d/, statusRingWidth),
    statusRingColor,
  ].join(' ');

  const containerClasses = [
    'relative inline-flex items-center justify-center overflow-visible bg-gray-100 dark:bg-gray-700',
    sizeClasses[size],
    shapeClasses[shape],
    className
  ].join(' ');

  const contentClasses = [
    'w-full h-full object-cover object-center flex items-center justify-center',
    shapeClasses[shape]
  ].join(' ');

  return (
    <div className={containerClasses}>
      {src && !imageError ? (
        <img
          src={src}
          alt={alt}
          className={contentClasses}
          onError={() => setImageError(true)}
        />
      ) : name ? (
        <span className="font-medium text-gray-600 dark:text-gray-300 select-none">
          {getInitials(name)}
        </span>
      ) : icon ? (
        <div className="flex items-center justify-center text-gray-500 dark:text-gray-400 w-1/2 h-1/2">
          {icon}
        </div>
      ) : (
        <svg
          className="w-[60%] h-[60%] text-gray-400 dark:text-gray-500"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      )}

      {status && (
        <span className={statusDotClasses} />
      )}
    </div>
  );
};